import {
  detectDelimiter,
  validateBulkPointModifierCsv,
  validateFile
} from '@/helpers/bulkPointModifierCsvValidator';
import {getSasToken} from '@/services/sasToken';
import {setStoreListUrl} from '@/store/slices/salesTargetSlice';
import {Constants} from '@/utils/constants/commonConstants';
import Papa from 'papaparse';

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

const parseCsvWithDelimiter = (file, delimiter) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      delimiter,
      header: true,
      skipEmptyLines: true,
      complete: (result) => resolve(result),
      error: (error) => reject(error)
    });
  });

const clearOptimisticUpload = (setFormData) => {
  setFormData((prev) => ({...prev, targetMediaFile: []}));
};

const restoreOrClearUpload = (setFormData, dispatch, uploadSnapshot) => {
  if (uploadSnapshot?.storeList) {
    dispatch(setStoreListUrl(uploadSnapshot.storeList));
    setFormData((prev) => ({
      ...prev,
      targetMediaFile: uploadSnapshot.targetMediaFile || [],
      storeList: uploadSnapshot.storeList
    }));
    return;
  }

  dispatch(setStoreListUrl(''));
  setFormData((prev) => ({
    ...prev,
    targetMediaFile: [],
    storeList: ''
  }));
};

const setValidationError = (
  setFileError,
  setFormData,
  dispatch,
  errors,
  uploadSnapshot
) => {
  setFileError({
    feedbackType: Constants.Error,
    helperText: errors.join('\n')
  });
  restoreOrClearUpload(setFormData, dispatch, uploadSnapshot);
};

export const handleBulkPointModifierCsvUpload = async (
  file,
  setFileError,
  {setFormData, dispatch, uploadFileOnBlob, uploadSnapshot = null}
) => {
  clearOptimisticUpload(setFormData);

  const fileValidation = validateFile(file);
  if (!fileValidation.isValid) {
    setValidationError(
      setFileError,
      setFormData,
      dispatch,
      fileValidation.errors,
      uploadSnapshot
    );
    return;
  }

  try {
    const fileText = await readFileAsText(file);
    const delimiter = detectDelimiter(fileText);
    const result = await parseCsvWithDelimiter(file, delimiter);
    const rows = result?.data;
    const headers = result?.meta?.fields;

    const {isInvalidFileData, errorsList} = validateBulkPointModifierCsv(
      file,
      rows,
      headers
    );

    if (isInvalidFileData) {
      setValidationError(
        setFileError,
        setFormData,
        dispatch,
        errorsList,
        uploadSnapshot
      );
      return;
    }

    const sasResponse = await getSasToken(file.name, Constants.BulkModifiers);
    if (!sasResponse?.blobUrl) {
      throw new Error(Constants.ErrorForFileUpload);
    }

    const response = await uploadFileOnBlob(sasResponse, file);
    const resolvedFileUrl =
      response?.media?.imageUrl ||
      sasResponse?.blobUrl ||
      response?.file?.fileUrl;

    if (response?.message !== Constants.Success || !resolvedFileUrl) {
      throw new Error(Constants.ErrorForFileUpload);
    }

    dispatch(setStoreListUrl(resolvedFileUrl));
    setFormData((prev) => ({
      ...prev,
      storeList: resolvedFileUrl,
      targetMediaFile: [file.name]
    }));
    setFileError({feedbackType: Constants.Info, helperText: ''});
  } catch {
    setValidationError(
      setFileError,
      setFormData,
      dispatch,
      ['Failed to process CSV file'],
      uploadSnapshot
    );
  }
};
